import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import axiosInstance from '../../../../axios/axiosInstance';
import apiEndpoints from '../../../../axios/api';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { getAccountLedgerReport } from '../../../../redux/actions/SocietyPortal/AccountLedgerReportAction/AccountLedgerReportAction';
import { FINANCIAL_YEARS, isRecoverableError } from './utils/helpers';
import FilterBar from './components/FilterBar';
import SummaryCards from './components/SummaryCards';
import LedgerTable from './components/LedgerTable';

const AccountLedgerReport = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();
    const printRef = useRef(null);

    const { accountLedgerReport: data, loading, error } = useSelector(
        state => state.accountLedgerReportReducer || {}
    );

    const defaultFY = FINANCIAL_YEARS[0];
    const [filters, setFilters] = useState({
        financialYear: defaultFY.value,
        accountCode: '',
        fromDate: defaultFY.fromDate,
        toDate: defaultFY.toDate,
    });

    const [accounts, setAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);

    // Fetch accounts dropdown on mount / society change
    useEffect(() => {
        if (!societyId) return;
        setAccountsLoading(true);
        const url = apiEndpoints.GET_CHART_OF_ACCOUNTS_DROPDOWN_API.replace(':societyId', societyId);
        axiosInstance.get(url)
            .then(res => setAccounts(res?.data || []))
            .catch(() => setAccounts([]))
            .finally(() => setAccountsLoading(false));
    }, [societyId]);

    const handleApply = useCallback(() => {
        if (!societyId || !filters.accountCode) return;
        dispatch(getAccountLedgerReport({
            societyId,
            accountCode: filters.accountCode,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
        }));
    }, [dispatch, societyId, filters]);
    usePageRefresh(handleApply);

    const handleReset = () => {
        setFilters({
            financialYear: defaultFY.value,
            accountCode: '',
            fromDate: defaultFY.fromDate,
            toDate: defaultFY.toDate,
        });
    };

    const selectedAccount = accounts.find(a => a.ac_code === filters.accountCode);
    const recoverableError = isRecoverableError(error) ? error : null;
    const genericError = error && !recoverableError ? error : null;

    const reportInfo = data?.reportInfo;
    const entries = data?.entries || [];
    const openingEntries = data?.openingEntries || [];
    const openingBalance = data?.openingBalance ?? 0;
    const totalDebit = data?.totalDebit ?? 0;
    const totalCredit = data?.totalCredit ?? 0;
    const closingBalance = data?.closingBalance ?? 0;

    const showResults = !!data && !loading;

    // ─── Excel Download ──────────────────────────────────────────────────────
    const handleExcelDownload = () => {
        if (!data) return;
        const header = ['Date', 'JV No', 'Cheque No', 'Narration', 'Contra Accounts', 'Dr Amount', 'Cr Amount', 'Balance'];

        const renderContra = (rels) =>
            (rels || []).map(r => `${r.accountCode || ''} - ${r.accountName || ''}`.trim()).filter(Boolean).join('; ');

        const openingRows = openingEntries.map(e => [
            e.date || '',
            e.journalNo || '',
            e.chequeNo || '',
            e.narration || '',
            renderContra(e.relatedAccounts),
            e.drAmount || 0,
            e.crAmount || 0,
            e.balance ?? 0,
        ]);
        const bodyRows = entries.map(e => [
            e.date || '',
            e.journalNo || '',
            e.chequeNo || '',
            e.narration || '',
            renderContra(e.relatedAccounts),
            e.drAmount || 0,
            e.crAmount || 0,
            e.balance ?? 0,
        ]);

        const wsData = [
            ['Account Ledger Report'],
            [selectedAccount
                ? `Account: ${selectedAccount.ac_code ? selectedAccount.ac_code + ' - ' : ''}${selectedAccount.name}`
                : (reportInfo ? `Account: ${reportInfo.accountCode} - ${reportInfo.accountName}` : '')],
            [`Period: ${reportInfo?.fromDate || filters.fromDate} to ${reportInfo?.toDate || filters.toDate}`],
            [],
        ];
        if (openingRows.length > 0 || openingBalance !== 0) {
            wsData.push(
                ['Opening Balance Breakdown'],
                header,
                ...openingRows,
                ['Opening Balance', '', '', '', '', '', '', openingBalance],
                [],
            );
        }
        wsData.push(
            ['Transactions'],
            header,
            ...bodyRows,
            ['Total', '', '', '', '', totalDebit, totalCredit, closingBalance],
        );

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = [14, 14, 14, 35, 30, 14, 14, 16].map(w => ({ wch: w }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Account Ledger');
        XLSX.writeFile(wb, `Account_Ledger_${reportInfo?.fromDate || filters.fromDate}_to_${reportInfo?.toDate || filters.toDate}.xlsx`);
    };

    // ─── Print ───────────────────────────────────────────────────────────────
    const handlePrint = () => {
        const content = printRef.current;
        if (!content) return;
        const win = window.open('', '_blank', 'width=1000,height=700');
        win.document.write(`
            <html>
            <head>
                <title>Account Ledger Report</title>
                <style>
                    * { box-sizing: border-box; margin: 0; padding: 0; }
                    body { font-family: Arial, sans-serif; font-size: 11px; color: #222; padding: 20px; }
                    h2 { font-size: 15px; margin-bottom: 4px; }
                    .meta { font-size: 10px; color: #666; margin-bottom: 14px; }
                    table { width: 100%; border-collapse: collapse; font-size: 10px; }
                    th { background: #f3f4f6; text-align: left; padding: 6px 8px; border: 1px solid #e5e7eb; font-weight: 700; font-size: 9px; text-transform: uppercase; letter-spacing: .04em; }
                    td { padding: 5px 8px; border: 1px solid #e5e7eb; }
                    tr.opening td { background: #fffbeb; font-weight: 600; color: #92400e; }
                    tr.total td { background: #f0f9ff; font-weight: 700; }
                    .text-right { text-align: right; }
                    @media print { @page { margin: 12mm; } }
                </style>
            </head>
            <body>
                <h2>Account Ledger Report</h2>
                <div class="meta">
                    ${reportInfo ? `Account: ${reportInfo.accountCode} - ${reportInfo.accountName}<br/>` : ''}
                    Period: ${reportInfo?.fromDate || filters.fromDate} to ${reportInfo?.toDate || filters.toDate}
                </div>
                ${content.innerHTML}
            </body>
            </html>
        `);
        win.document.close();
        win.focus();
        setTimeout(() => { win.print(); win.close(); }, 400);
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-4">
                <div>
                    <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">Account Ledger Report</h1>
                    <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">
                        {reportInfo
                            ? `Showing ledger for: ${reportInfo.accountCode} · ${reportInfo.accountName}`
                            : selectedAccount
                                ? `Selected: ${selectedAccount.ac_code ? selectedAccount.ac_code + ' · ' : ''}${selectedAccount.name}`
                                : 'Select filters and click Apply to view account ledger'}
                    </p>
                </div>
                {showResults && (
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <button
                            onClick={handlePrint}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors"
                        >
                            <Icon icon="mdi:printer-outline" className="tw-text-sm" />
                            Print
                        </button>
                        <button
                            onClick={handleExcelDownload}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-emerald-700 tw-border tw-border-emerald-200 tw-bg-emerald-50 tw-rounded-lg hover:tw-bg-emerald-100 tw-transition-colors"
                        >
                            <Icon icon="mdi:file-excel-outline" className="tw-text-sm" />
                            Excel Download
                        </button>
                    </div>
                )}
            </div>

            {/* Filters */}
            <FilterBar
                filters={filters}
                setFilters={setFilters}
                accounts={accounts}
                accountsLoading={accountsLoading}
                onApply={handleApply}
                onReset={handleReset}
            />

            {/* Inline (recoverable) error — sits next to the form */}
            {recoverableError && !loading && (
                <div className="tw-mb-4 tw-px-3 tw-py-2 tw-text-xs tw-text-red-700 tw-bg-red-50 tw-border tw-border-red-100 tw-rounded-lg">
                    <Icon icon="mdi:alert-circle-outline" className="tw-inline tw-text-sm tw-mr-1.5 tw--mt-0.5" />
                    {recoverableError}
                </div>
            )}

            {/* Loading */}
            {loading && (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20">
                    <div className="tw-w-10 tw-h-10 tw-rounded-full tw-border-2 tw-border-blue-100 tw-border-t-blue-600 tw-animate-spin tw-mb-3" />
                    <p className="tw-text-xs tw-text-gray-400">Loading ledger data…</p>
                </div>
            )}

            {/* Generic error */}
            {genericError && !loading && (
                <div className="tw-py-12 tw-text-center tw-text-sm tw-text-red-600">
                    {genericError}
                </div>
            )}

            {/* Results */}
            {showResults && (
                <div ref={printRef}>
                    <SummaryCards
                        openingBalance={openingBalance}
                        totalDebit={totalDebit}
                        totalCredit={totalCredit}
                        closingBalance={closingBalance}
                    />
                    <LedgerTable
                        entries={entries}
                        openingEntries={openingEntries}
                        openingBalance={openingBalance}
                        openingTotalDebit={data?.openingTotalDebit ?? 0}
                        openingTotalCredit={data?.openingTotalCredit ?? 0}
                        totalDebit={totalDebit}
                        totalCredit={totalCredit}
                        closingBalance={closingBalance}
                        reportInfo={reportInfo}
                    />
                </div>
            )}

            {/* Empty state */}
            {!loading && !data && !error && (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20">
                    <Icon icon="mdi:file-document-outline" className="tw-text-5xl tw-text-gray-200" />
                    <p className="tw-text-gray-400 tw-mt-2 tw-text-sm">
                        Select an account, choose a date range, and click <span className="tw-font-semibold">Apply</span>.
                    </p>
                </div>
            )}
        </>
    );
};

export default AccountLedgerReport;
